import { type ApiError, usePreflightBatch, useStartBatch } from '@ctcells/api'
import type { PreflightResult } from '@ctcells/contract'
import { errorMessage } from '@ctcells/i18n'
import { useState } from 'react'

import { startGateFromError, startGateFromPreflight, type StartGate } from './startGate.ts'

/**
 * **배치 시작 한 번의 전 과정** — 사전 점검 → (차단 | 경고 확약 | 바로 시작) → 명령.
 *
 * 🔴 **[시작]을 잇는 자리는 여기 하나다.** 작업 줄이 쓰고, 「테스트 작업 시작」 같은
 *    다른 시작 창구도 같은 것을 쓴다 — 갈라 지으면 한쪽만 확약을 싣게 되고, 그 순간
 *    서버가 409 로 거절하거나(`warn_ack_required`) **무엇을 알고 시작했는지가 원장에서 빈다**.
 *
 * 흐름
 * ```
 * begin()  →  POST /batches/{id}/preflight
 *               ├ passed=false      → gate.phase='blocked'  (시작 안 함 · 확정 버튼 없음)
 *               ├ 경고 0건/미탑재   → 바로 POST start (확약 키 없음)
 *               └ 경고 1건 이상     → gate.phase='warn'      (사람이 확인해야 다음이 있다)
 * confirm(codes) → POST start {command_id, warn_ack, acknowledged_warnings}
 *               └ 409 확약 축       → 서버가 준 정답으로 목록을 갈아 «다시 보인다»
 *                                     (🔴 자동 재시도 없음 — 사람이 다시 누른다)
 * ```
 *
 * 🔴 **막는 축은 `passed` 하나다** — 자세한 사정은 `startGate.ts` 머리말.
 * ⚠ `POST /batches/{id}/preflight` 는 **제어락이 필요 없다**(실측 2026-09-22 — 그 라우터만
 *   `require_control_lock` 의존이 없다). 기록을 바꾸지 않는 쓰기라 눌러도 안전하다.
 * ⚠ 시작은 서버가 **같은 트랜잭션에서 Pre-flight 를 다시 잰다**(`SM-BATCH-07`) — 그래서
 *   이 훅이 통과시켰다고 시작이 보장되지 않는다. 확약 목록도 **그 시점** 값과 대조된다.
 */
export interface UseStartGateOptions {
  /** 시작할 작업. 없으면 아무것도 나가지 않는다 */
  batchId: string | undefined
  /** 다른 명령이 날아가 있는 등 지금 누르면 안 되는 사정 */
  blocked?: boolean | undefined
  /**
   * 확약 축이 **아닌** 실패의 문구. 🔴 확약 축 실패는 여기로 오지 않는다 — 그건 대화가
   * 정정 목록으로 말하고, 이 통로로도 보내면 같은 일을 두 곳에서 두 번 말하게 된다.
   */
  onError: (message: string) => void
  /** 명령을 쏘기 직전(이전 실패 문구를 비우는 자리) */
  onSend?: (() => void) | undefined
  /**
   * 시작 명령이 받아들여졌다(202). ⚠ **시작됐다는 뜻이 아니다** — 결과는 구독으로 온다.
   * 🔴 여기서 **캐시를 손대지 마라.** 무효화는 `createCommand` 가 이미 했고, 화면이 앞질러
   *    「돌고 있다」를 그리면 그것이 낙관적 갱신이다(DP-04). 쓰는 자리는 「이 창구의 한 회차가
   *    끝났다」를 아는 것뿐이다(개발용 바로 돌리기의 진행 글자).
   */
  onStarted?: (() => void) | undefined
}

export interface StartGateControls {
  /** 열려 있는 게이트. `null` 이면 대화가 없다 */
  gate: StartGate | null
  /** [시작] 클릭 — 1단계(사전 점검)를 부른다 */
  begin: () => void
  /** 확약 대화의 확정 — 2단계(명령). 코드 **전부**를 그대로 넘긴다 */
  confirm: (codes: readonly string[]) => void
  /** 대화 닫기(취소). 🔴 시작은 나가지 않는다 */
  close: () => void
  /**
   * 시작 창 안에서 다시 돈 사전 점검의 결과를 받아 «같은 창»의 다음 얼굴로 넘어간다(eric 2026-10-23 「팝업창에서.. 다 해결」).
   * `batchId` — 그 결과를 낸 작업(창이 작업을 다시 만들었으면 새 작업). 이후 확정(`confirm`)은 그 작업으로 나간다.
   * 🔴 조치 후 재점검은 자동 시작하지 않는다 — 통과해도 사람이 [시작]을 다시 눌러야 한다.
   */
  adopt: (result: PreflightResult, batchId: string) => void
  /** 사전 점검 또는 시작이 날아가 있는가 */
  pending: boolean
  /** 사전 점검(`POST /batches/{id}/preflight`)이 날아가 있는가 — 작업 줄 「00 사전 점검」 칸의 「점검 중」 */
  checking: boolean
  /**
   * 시작 명령(`POST /batches/{id}/start`)이 날아가 있는가. 🔴 시작은 사전 점검이 `passed` 인 뒤에만 나간다 — 경고 0건이면 창 없이
   * 바로 나가므로, 그 사이 「00 사전 점검」 칸은 이 값으로 「통과」를 말한다(결과를 따로 쥐지 않는다)
   */
  starting: boolean
}

export function useStartGate({
  batchId,
  blocked = false,
  onError,
  onSend,
  onStarted,
}: UseStartGateOptions): StartGateControls {
  const preflightBatch = usePreflightBatch()
  const startBatch = useStartBatch()
  const [gate, setGate] = useState<StartGate | null>(null)
  /**
   * 창이 작업을 다시 만들었을 때의 대상 — 🔴 작업 줄의 `batchId` 는 목록이 다시 올 때까지 옛 작업일 수 있다.
   * 그 사이 확정이 옛 작업으로 나가면 안 된다. `begin` 이 새로 시작하면 비운다
   */
  const [adopted, setAdopted] = useState<string | undefined>(undefined)
  const target = adopted ?? batchId

  const pending = preflightBatch.isPending || startBatch.isPending

  /**
   * 2단계. `codes` 가 비어 있으면 확약 키를 **싣지 않는다**
   * (`useStartBatch` 안의 `startWarnAck()` 이 정한다 — 여기서 중복 제거·정렬하지 않는다).
   */
  const confirm = (codes: readonly string[], to: string | undefined = target) => {
    if (to === undefined) return
    onSend?.()
    startBatch.mutate(
      { batchId: to, ...(codes.length === 0 ? {} : { acknowledgedWarnings: codes }) },
      {
        onSuccess: () => {
          setGate(null)
          onStarted?.()
        },
        onError: (error: ApiError) => {
          const corrected = startGateFromError(error)
          // 🔴 자동 재시도 없음 — 정정 목록을 보이고 사람이 다시 누른다
          if (corrected === null) onError(errorMessage(error))
          else setGate(corrected)
        },
      },
    )
  }

  const begin = () => {
    if (batchId === undefined || blocked || pending) return
    onSend?.()
    setGate(null)
    setAdopted(undefined)
    preflightBatch.mutate(
      { batchId },
      {
        onError: (error: ApiError) => {
          onError(errorMessage(error))
        },
        onSuccess: (result) => {
          const next = startGateFromPreflight(result)
          // 게이트가 없으면 대화를 세우지 않고 바로 보낸다 — 손이 한 번 더 가지 않게
          if (next === null) confirm([], batchId)
          else setGate(next)
        },
      },
    )
  }

  const adopt = (result: PreflightResult, resultBatchId: string) => {
    setAdopted(resultBatchId)
    const next = startGateFromPreflight(result)
    setGate(next ?? { phase: 'confirm', result })
  }

  return {
    gate,
    begin,
    confirm,
    close: () => {
      setGate(null)
    },
    adopt,
    pending,
    checking: preflightBatch.isPending,
    starting: startBatch.isPending,
  }
}
